<template>
  <div class="container">
    <div v-if="label" class="label-row">
      <label class="label truncate" :style="{ color: labelColor }" :title="label">
        {{ label }}<span v-if="required" class="ml-0.5">*</span>
      </label>
      <span v-if="helpText" class="info-tooltip" tabindex="0">
        <svg xmlns="http://www.w3.org/2000/svg" class="info-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20A10 10 0 0012 2z" />
        </svg>
        <span class="tooltip-text">{{ helpText }}</span>
      </span>
    </div>
    <div class="input-container shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]">
      <!-- Renderiza el icono si se proporciona -->
      <component v-if="icon" :is="icon" class="icon" />
      <input
        class="input truncate"
        :class="{ 'border-red-500': isInvalid, 'opacity-50 cursor-not-allowed': disabled }"
        :type="type"
        :placeholder="placeholder"
        :style="{ '--placeholder-color': placeholderColor, backgroundColor: bgColor, color: textColor }"
        :value="inputValue"
        @input="onInput"
        :readonly="readonly"
        :disabled="disabled"
        :title="inputValue"
      />
      <slot />
    </div>
    <p class="text-red-500 text-xs pt-1 min-h-[20px]">{{ error }}</p>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
  placeholder: String,
  label: String,
  modelValue: [Number, String],
  icon: [Object, Function],
  type: {
    type: String,
    default: 'text',
  },
  labelColor: String,
  placeholderColor: String,
  bgColor: String,
  textColor: String,
  error: {
    type: String,
    default: ''
  },
  helpText: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  },
  required: {
    type: Boolean,
    default: false
  },
  readonly: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:modelValue']);
const inputValue = ref(props.modelValue ?? '');
const isInvalid = ref(false);

// Actualiza el valor local cuando cambia desde fuera
watch(() => props.modelValue, (val) => {
  inputValue.value = val;
});

// Verifica y emite en cada cambio del input
const onInput = (e) => {
  const value = e.target.value;
  inputValue.value = value;
  emit('update:modelValue', value);

  if (props.type === 'email') {
    isInvalid.value = !validateEmail(value);
  } else {
    isInvalid.value = false;
  }
};

function validateEmail(email) {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
}
</script>
<style scoped>
.container {
  margin: 8px 0;
}

.label-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  min-width: 0;
}

.label {
  font-size: 16px;
  margin-bottom: 0;
  color: #385660;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: help;
}

.info-tooltip {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  color: var(--c-primary, #25b0d7);
  cursor: help;
}

.info-icon {
  width: 16px;
  height: 16px;
}

.tooltip-text {
  display: none;
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  width: max-content;
  max-width: 240px;
  background-color: #385660;
  color: #fff;
  font-size: 12px;
  line-height: 1.4;
  padding: 6px 10px;
  border-radius: 6px;
  white-space: normal;
  z-index: 10;
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.25);
}

.info-tooltip:hover .tooltip-text,
.info-tooltip:focus .tooltip-text,
.info-tooltip:focus-visible .tooltip-text {
  display: block;
}

.input-container {
  display: flex;
  align-items: center;
  border: 1px solid #3A5762;
  border-radius: 8px;
  padding: 0 12px;
  overflow: hidden;
}

.icon {
  margin-right: 10px;
}

.input {
  flex: 1;
  padding: 8px 0;
  height: 40px;
  font-size: 14px;
  border: none;
  outline: none;
  color: #7CADB9;
  transition: all 0.3s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: help;
  min-width: 0;
}

.input:disabled {
  background-color: #F5F5F5 !important;
  color: #9E9E9E !important;
  cursor: not-allowed;
  border: 1px solid #E0E0E0;
}

.input-container:has(.input:disabled) {
  background-color: #F5F5F5;
  border-color: #E0E0E0;
  opacity: 0.8;
}

.input::placeholder {
  color: #B0BEC5;
}
</style>
